import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let bodyLockCount = 0;
let previousBodyOverflow = '';
const dialogStack: HTMLElement[] = [];

function lockBodyScroll() {
  if (bodyLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyLockCount += 1;
}

function unlockBodyScroll() {
  bodyLockCount = Math.max(0, bodyLockCount - 1);
  if (bodyLockCount === 0) document.body.style.overflow = previousBodyOverflow;
}

/** Shared focus, Escape and scroll behaviour for modal application surfaces. */
export function useDialogInteraction<T extends HTMLElement>(
  open: boolean,
  onClose: () => void,
): RefObject<T> {
  const dialogRef = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Capture during render, before a descendant's autoFocus runs in the commit.
  if (
    open &&
    previousFocusRef.current === null &&
    typeof document !== 'undefined' &&
    document.activeElement instanceof HTMLElement
  ) {
    previousFocusRef.current = document.activeElement;
  }

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) previousFocusRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    lockBodyScroll();
    if (dialog) dialogStack.push(dialog);

    const focusableElements = () =>
      dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
            const style = window.getComputedStyle(element);
            return (
              !element.hidden &&
              element.getAttribute('aria-hidden') !== 'true' &&
              style.display !== 'none' &&
              style.visibility !== 'hidden'
            );
          })
        : [];

    const focusTarget = focusableElements()[0] ?? dialog;
    focusTarget?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (dialog && dialogStack.at(-1) !== dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;

      const focusable = focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (dialog) {
        const index = dialogStack.lastIndexOf(dialog);
        if (index >= 0) dialogStack.splice(index, 1);
      }
      unlockBodyScroll();
      previousFocusRef.current?.focus();
    };
  }, [open]);

  return dialogRef;
}
